// frontend/src/components/TextViewer.tsx
// Changes:
// - Improved search feature to highlight entire matches more clearly, similar to Windows Ctrl+F
// - Added distinct styling for the currently active match (e.g., border and different background)
// - Preserved all original logic, including navigate(-1) for the "Back" button

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Chip,
  TextField,
  Snackbar,
  FormControl,
  Select,
  SelectChangeEvent,
  MenuItem,
  InputLabel,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Home as HomeIcon,
  ContentCopy as CopyIcon,
  Print as PrintIcon,
  Code as CodeIcon,
  TextSnippet as TextIcon, 
  Description as DocumentIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  FindInPage as FindIcon,
  Info as InfoIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon
} from '@mui/icons-material';
import { 
  fetchTextContent, 
  getPresignedDownloadUrl, 
  getFileMetadata,
  getFileName,
  apiLogger
} from '../services/api';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';

const COMPONENT_CONTEXT = 'TextViewer';
const MAX_SAFE_TEXT_SIZE = 500 * 1024; // 500KB
const INITIAL_CHUNK_SIZE = 50 * 1024; // 50KB
const CHUNK_SIZE = 100 * 1024; // 100KB
const DEFAULT_FONT_SIZE = 14;

const TextViewer: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const decodedKey = key ? decodeURIComponent(key) : '';
  const fileName = getFileName(decodedKey);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [textData, setTextData] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [progressiveLoading, setProgressiveLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<any>(null);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [isLargeFile, setIsLargeFile] = useState<boolean>(false);
  const [fileFormat, setFileFormat] = useState<string>('text');
  const [totalSize, setTotalSize] = useState<number>(0);
  const [filterValue, setFilterValue] = useState<string>('');
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [fontSize, setFontSize] = useState<number>(DEFAULT_FONT_SIZE);
  const [searchMatches, setSearchMatches] = useState<number>(0);
  const [currentMatch, setCurrentMatch] = useState<number>(0);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  useEffect(() => {
    apiLogger.info(COMPONENT_CONTEXT, `Mounted text viewer for file: ${decodedKey}`);
    return () => {
      apiLogger.info(COMPONENT_CONTEXT, `Unmounted text viewer for file: ${decodedKey}`);
    };
  }, [decodedKey]);

  const fetchMetadata = useCallback(async () => {
    if (!decodedKey) return;
    try {
      const result = await getFileMetadata(decodedKey);
      setMetadata(result.metadata);
      if (result.metadata && result.metadata.contentLength) {
        setTotalSize(result.metadata.contentLength);
      }
    } catch (err) {
      apiLogger.warn(COMPONENT_CONTEXT, `Failed to load metadata for ${decodedKey}`, err);
    }
  }, [decodedKey]);

  const detectFileFormat = useCallback((content: string, fileName: string) => {
    if (content.trim().startsWith('<?xml') || (content.trim().startsWith('<') && content.includes('</'))) {
      return 'xml';
    }
    try {
      JSON.parse(content);
      return 'json';
    } catch {
      // Not JSON
    }
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension) {
      if (['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'c', 'cpp', 'cs', 'php', 'rb', 'go', 'rust', 'scala', 'swift'].includes(extension)) {
        return 'code';
      }
      if (['json', 'yaml', 'yml', 'xml', 'html', 'htm', 'svg'].includes(extension)) {
        return 'code';
      }
      if (extension === 'opus') {
        return 'opus';
      }
      if (extension === 'log') {
        return 'log';
      }
    }
    return 'text';
  }, []);

  const highlightCode = useCallback((code: string, format: string) => {
    if (format === 'text' || format === 'log') return code;
    try {
      if (format === 'xml' || format === 'opus') {
        if (!hljs.getLanguage('xml')) throw new Error('XML language not supported');
        return hljs.highlight(code, { language: 'xml' }).value;
      }
      if (format === 'json') {
        if (!hljs.getLanguage('json')) throw new Error('JSON language not supported');
        return hljs.highlight(code, { language: 'json' }).value;
      }
      if (format === 'code') {
        return hljs.highlightAuto(code).value;
      }
      return code;
    } catch (err) {
      apiLogger.warn(COMPONENT_CONTEXT, `Highlighting failed for format ${format}: ${err}`, err);
      return code;
    }
  }, []);

  const loadTextProgressively = useCallback(async (textContent: string, initialChunkSize: number, chunkSize: number) => {
    setTextData(textContent.slice(0, initialChunkSize));
    if (textContent.length <= initialChunkSize) return;

    setProgressiveLoading(true);
    const totalChunks = Math.ceil((textContent.length - initialChunkSize) / chunkSize);

    const loadChunk = async (startIndex: number, chunkIndex: number) => {
      if (chunkIndex >= totalChunks) {
        setProgressiveLoading(false);
        const format = detectFileFormat(textContent, fileName);
        setFileFormat(format);
        apiLogger.info(COMPONENT_CONTEXT, `Completed progressive loading, format detected: ${format}`);
        return;
      }

      const nextEndIndex = Math.min(startIndex + chunkSize, textContent.length);
      setTextData(prev => prev + textContent.slice(startIndex, nextEndIndex));
      await new Promise(resolve => setTimeout(resolve, 50));
      await loadChunk(nextEndIndex, chunkIndex + 1);
    };

    await loadChunk(initialChunkSize, 0);
  }, [detectFileFormat, fileName]);

  useEffect(() => {
    async function loadTextData() {
      if (!decodedKey) {
        setError('No file specified');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        apiLogger.info(COMPONENT_CONTEXT, `Fetching text content for: ${decodedKey}`);
        const response = await fetchTextContent(decodedKey);
        const textContent = response.data;

        if (!textContent) {
          setError('No text content received');
          setLoading(false);
          return;
        }

        const isLarge = textContent.length > MAX_SAFE_TEXT_SIZE;
        setIsLargeFile(isLarge);

        if (isLarge) {
          apiLogger.warn(COMPONENT_CONTEXT, `Large text file detected (${textContent.length} bytes), loading progressively`);
          await loadTextProgressively(textContent, INITIAL_CHUNK_SIZE, CHUNK_SIZE);
        } else {
          setTextData(textContent);
          const format = detectFileFormat(textContent, fileName);
          setFileFormat(format);
          apiLogger.info(COMPONENT_CONTEXT, `Format detected: ${format}`);
        }

        setTotalSize(textContent.length);
        setError(null);
        fetchMetadata();
        apiLogger.info(COMPONENT_CONTEXT, `Successfully loaded text with ${textContent.length} bytes`);
      } catch (err) {
        apiLogger.error(COMPONENT_CONTEXT, `Error fetching text data: ${err}`, err);
        setError(`Failed to load text file: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setLoading(false);
      }
    }
    loadTextData();
  }, [decodedKey, fetchMetadata, loadTextProgressively, detectFileFormat, fileName]);

  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      apiLogger.info(COMPONENT_CONTEXT, `Downloading file: ${decodedKey}`);
      const response = await getPresignedDownloadUrl(decodedKey);
      const link = document.createElement('a');
      link.href = response.url;
      link.download = response.filename || fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      apiLogger.error(COMPONENT_CONTEXT, `Error downloading text file: ${err}`, err);
      setError(`Failed to download file: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(textData)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        showSnackbar('Text copied to clipboard');
      })
      .catch(err => {
        apiLogger.error(COMPONENT_CONTEXT, `Failed to copy text: ${err}`, err);
        showSnackbar('Failed to copy text');
      });
  };

  const handlePrint = () => {
    try {
      apiLogger.info(COMPONENT_CONTEXT, `Printing file: ${fileName}`);
      const printWindow = window.open('', '_blank');
      if (!printWindow) throw new Error('Failed to open print window');
      printWindow.document.write(`
        <html>
          <head>
            <title>${fileName}</title>
            <style>
              body { font-family: monospace; white-space: pre-wrap; padding: 20px; line-height: 1.5; font-size: ${fontSize}px; ${darkMode ? 'background-color: #121212; color: #e0e0e0;' : ''} }
              h1 { font-family: sans-serif; border-bottom: 1px solid #ccc; padding-bottom: 10px; ${darkMode ? 'color: #ffffff;' : ''} }
              .highlighted { background-color: ${darkMode ? '#005b9f' : '#ffff00'}; ${darkMode ? 'color: #ffffff;' : 'color: #000000;'} }
              .highlighted-active { background-color: ${darkMode ? '#007bff' : '#ffeb3b'}; border: 1px solid ${darkMode ? '#ffffff' : '#000000'}; }
            </style>
          </head>
          <body>
            <h1>${fileName}</h1>
            ${formatTextForDisplay(true)}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } catch (err) {
      apiLogger.error(COMPONENT_CONTEXT, `Print error: ${err}`, err);
      showSnackbar('Failed to open print window');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleFontSizeChange = (event: SelectChangeEvent<number>) => {
    setFontSize(Number(event.target.value));
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event.target.value);
    setCurrentMatch(0); // Reset to first match
    if (!event.target.value) {
      setSearchMatches(0);
      return;
    }
    try {
      const regex = new RegExp(event.target.value, 'gi');
      const matches = (textData.match(regex) || []).length;
      setSearchMatches(matches);
    } catch (err) {
      setSearchMatches(0);
    }
  };

  const scrollToMatch = (direction: 'next' | 'prev') => {
    if (!filterValue || searchMatches === 0 || !containerRef.current) return;
    const spans = containerRef.current.querySelectorAll('.highlighted');
    if (spans.length === 0) return;

    // Remove the active class from all spans
    spans.forEach(span => span.classList.remove('highlighted-active'));

    const newMatch = direction === 'next'
      ? Math.min(currentMatch + 1, searchMatches - 1)
      : Math.max(currentMatch - 1, 0);
    
    setCurrentMatch(newMatch);
    const span = spans[newMatch] as HTMLElement;
    span.classList.add('highlighted-active'); // Add active class to the current match
    span.scrollIntoView({ behavior: 'smooth', block: 'center' });
    showSnackbar(`Match ${newMatch + 1} of ${searchMatches}`);
  };

  const handleToggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatTextForDisplay = (forPrint: boolean = false) => {
    let content = textData.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); // Proper HTML escaping
    if (fileFormat !== 'text' && !forPrint) {
      content = highlightCode(content, fileFormat);
    }
    if (filterValue && !forPrint) {
      try {
        const regex = new RegExp(`(${filterValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        content = content.replace(regex, (match, p1, offset) => {
          const matchIndex = (textData.toLowerCase().indexOf(filterValue.toLowerCase(), offset) === offset) ? 
            Array.from(textData.toLowerCase().substr(0, offset).matchAll(new RegExp(filterValue.toLowerCase(), 'g'))).length : 0;
          const isActive = matchIndex === currentMatch;
          return `<span class="highlighted${isActive ? ' highlighted-active' : ''}">${match}</span>`;
        });
      } catch (err) {
        apiLogger.warn(COMPONENT_CONTEXT, `Search highlight error: ${err}`, err);
      }
    }
    return content;
  };

  const renderMetadata = () => {
    if (!metadata && !totalSize) return null;
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>File Information</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {metadata?.mimeType && <Chip size="small" label={`Type: ${metadata.mimeType}`} variant="outlined" />}
          {(metadata?.contentLength || totalSize > 0) && (
            <Chip size="small" label={`Size: ${((metadata?.contentLength || totalSize) / 1024).toFixed(2)} KB`} variant="outlined" />
          )}
          {metadata?.lastModified && (
            <Chip size="small" label={`Modified: ${new Date(metadata.lastModified).toLocaleDateString()}`} variant="outlined" />
          )}
          <Chip size="small" label={`Format: ${fileFormat.toUpperCase()}`} color="primary" variant="outlined" />
          {textData && (
            <Chip size="small" label={`Lines: ${textData.split('\n').length}`} color="primary" variant="outlined" />
          )}
        </Box>
      </Box>
    );
  };

  const getFileIcon = () => {
    switch (fileFormat) {
      case 'code': return <CodeIcon />;
      case 'json': return <CodeIcon />;
      case 'xml': return <CodeIcon />;
      case 'opus': return <DocumentIcon />;
      case 'log': return <TextIcon />;
      default: return <TextIcon />;
    }
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'hidden', p: 2 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" /> Home
        </Link>
        <Typography color="text.primary">{fileName}</Typography>
      </Breadcrumbs>
      
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" component="h1">{fileName}</Typography>
            <Chip icon={getFileIcon()} label={fileFormat.toUpperCase()} color="primary" variant="outlined" size="small" />
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Tooltip title="Search in text">
              <IconButton color="primary" onClick={() => setShowFilter(!showFilter)} size="small" aria-label="Toggle search">
                {showFilter ? <CloseIcon /> : <FindIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
              <IconButton color="primary" onClick={handleCopy} size="small" aria-label="Copy to clipboard">
                <CopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton color="primary" onClick={handlePrint} size="small" aria-label="Print">
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Button 
              variant="contained" 
              startIcon={downloadLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleDownload}
              size="small"
              disabled={downloadLoading}
              aria-label="Download file"
            >
              Download
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              size="small"
              aria-label="Back to previous page"
            >
              Back
            </Button>
            {metadata && (
              <Tooltip title="View file metadata">
                <IconButton color="primary" onClick={() => alert(JSON.stringify(metadata, null, 2))} aria-label="View file metadata">
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
        
        {renderMetadata()}
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {showFilter && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search in text..."
                value={filterValue}
                onChange={handleFilterChange}
                InputProps={{
                  endAdornment: filterValue ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ mr: 1 }}>{currentMatch + 1}/{searchMatches} matches</Typography>
                      <IconButton size="small" onClick={() => scrollToMatch('prev')} disabled={searchMatches === 0 || currentMatch === 0} aria-label="Previous match">
                        <NavigateBeforeIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => scrollToMatch('next')} disabled={searchMatches === 0 || currentMatch === searchMatches - 1} aria-label="Next match">
                        <NavigateNextIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setFilterValue('')} edge="end" aria-label="Clear search">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : null,
                }}
                aria-label="Search in text"
              />
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <Tooltip title={`Switch to ${darkMode ? 'light' : 'dark'} mode`}>
              <IconButton color="primary" onClick={handleToggleDarkMode} size="small" aria-label={`Switch to ${darkMode ? 'light' : 'dark'} mode`}>
                {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="font-size-label">Font Size</InputLabel>
              <Select
                labelId="font-size-label"
                value={fontSize}
                onChange={handleFontSizeChange}
                label="Font Size"
              >
                <MenuItem value={12}>Small</MenuItem>
                <MenuItem value={14}>Medium</MenuItem>
                <MenuItem value={16}>Large</MenuItem>
                <MenuItem value={18}>X-Large</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : textData.length === 0 ? (
          <Alert severity="info">This file appears to be empty.</Alert>
        ) : (
          <>
            {isLargeFile && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This is a large text file ({(totalSize / 1024).toFixed(2)} KB). 
                {progressiveLoading ? ' Loading content progressively...' : ''}
              </Alert>
            )}
            
            <Card 
              variant="outlined" 
              sx={{ maxHeight: '70vh', overflow: 'auto', bgcolor: darkMode ? '#1a1a1a' : '#fff' }}
              ref={containerRef}
            >
              <CardContent sx={{ p: 0 }}>
                <pre
                  style={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                    padding: '16px',
                    margin: 0,
                    fontSize: `${fontSize}px`,
                    backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                    color: darkMode ? '#e0e0e0' : '#000',
                    lineHeight: 1.5,
                    border: darkMode ? '1px solid #333' : 'none',
                    borderRadius: '4px',
                  }}
                >
                  <style>
                    {`
                      .highlighted {
                        background-color: ${darkMode ? '#005b9f' : '#ffff00'};
                        ${darkMode ? 'color: #ffffff;' : 'color: #000000;'}
                        padding: 2px 4px;
                        border-radius: 3px;
                        display: inline-block;
                      }
                      .highlighted-active {
                        background-color: ${darkMode ? '#007bff' : '#ffeb3b'};
                        border: 1px solid ${darkMode ? '#ffffff' : '#000000'};
                        box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
                      }
                    `}
                  </style>
                  <span dangerouslySetInnerHTML={{ __html: formatTextForDisplay() }} />
                </pre>
              </CardContent>
            </Card>
            
            {progressiveLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 1 }}>Loading more content...</Typography>
              </Box>
            )}
          </>
        )}
        
        {fileFormat === 'opus' && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="info">
              <Typography variant="body2">
                This is an OPUS command file used by the Open Bay lab machine. It contains configuration and instructions in XML format.
              </Typography>
            </Alert>
          </Box>
        )}
      </Paper>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default TextViewer;